import { ChangeEvent } from "react";
import { ExtensionTypes } from "../../features/reimbursement/types";

export const triggerFileInput = (inputId: string) => {
  document.getElementById(inputId)?.click();
};

// Reusable function for handling file changes
export const handleFileChange = (
  event: ChangeEvent<HTMLInputElement>,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  fieldName: string,
  multiple: boolean = false,
  prevFiles?: File[]
) => {
  const files = event.target.files;
  if (files && files.length > 0) {
    if (multiple) {
      // Convert FileList to an array and set it
      setFieldValue(fieldName, [...(prevFiles || []), ...Array.from(files)]);
    } else {
      setFieldValue(fieldName, files[0]); // Single file
    }
  }
};

export const handleDownload = (
  imageUrl: string,
  filename: string = "image.jpg"
) => {
  const link = document.createElement("a");
  link.href = imageUrl;
  link.target = "_blank";
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const removeAttachment = (
  fileIndex: number,
  fieldName: string,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void,
  currentFiles: File[]
) => {
  const updatedFiles = currentFiles.filter((_, index) => index !== fileIndex);
  setFieldValue(fieldName, updatedFiles);
};

export const flattenTouched = (touched: any): { [key: string]: boolean } => {
  const result: { [key: string]: boolean } = {};
  for (const key in touched) {
    if (typeof touched[key] === "object" && touched[key] !== null) {
      result[key] = false; // Default handling for nested types
    } else {
      result[key] = touched[key];
    }
  }
  return result;
};

type ExtensionCategory = keyof ExtensionTypes; // 'img' | 'pdf' | 'excel'

export const findAndCategorizedAttachment = (attachment: string) => {
  const categorizedFiles: ExtensionTypes = {
    img: [],
    pdf: [],
    excel: [],
  };

  const extensionsMap: Record<string, ExtensionCategory> = {
    jpeg: "img",
    jpg: "img",
    png: "img",
    webp: "img",
    gif: "img",
    svg: "img",
    pdf: "pdf",
    xls: "excel",
    xlsx: "excel",
    xlsm: "excel",
    xlsb: "excel",
    csv: "excel",
  };
  if (attachment) {
    // First safely make it an array
    const attachmentsArray = attachment.includes(",")
      ? attachment.split(",")
      : [attachment];

    attachmentsArray.forEach((file: string) => {
      const extension = file.split(".").pop()?.toLowerCase();
      const category = extension && extensionsMap[extension];
      if (category) {
        categorizedFiles[category]?.push(file);
      }
    });
  }

  return categorizedFiles;
};

export const canvasToFile = async (
  canvas: HTMLCanvasElement,
  fileName: string,
  mimeType = "image/jpeg",
  quality = 0.9
): Promise<File> => {
  return new Promise((resolve) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          throw new Error("Canvas is empty");
        }
        const file = new File([blob], fileName, { type: mimeType });
        resolve(file);
      },
      mimeType,
      quality
    );
  });
};
